import { State, Action, StateContext, Store } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { NewsfeedService, PostSearchFilter } from '@support/services/newsfeed.service';
import { FeatureFeedback, Post, PostWrapper } from '@support/models/post.model';
import {
  NewsfeedGetPostAction,
  NewsfeedGetPostErrorAction,
  NewsfeedGetPostSuccessAction,
  PostSearchAction,
  PostSearchFailedAction,
  PostSearchSuccessAction,
  AppendNewPostsAction,
  AppendNewPostsSuccessAction,
  AppendNewPostsFailedAction,
  GetCombinedPostsAction,
  GetCombinedPostsSuccessAction,
  GetCombinedPostsFailedAction,
  GetFeatureFeedbacksAction,
  GetFeatureFeedbacksSuccessAction,
} from './newsfeed.actions';

import { NotificationService } from '@core/services/notification.service';
import { addRefToPost, alternateDistributionOfPosts } from './newsfeed-utils';

enum Messages {
  GetLatestPostsFailed = 'Feil ved henting av nyeste artikler',
  GetPostSearchFailed = 'Feil ved henting av artikler',
  GetPostFailed = 'Feil ved henting av artikkel',
}

export interface NewsfeedStateModel {
  posts: Post[];
  combinedPosts: PostWrapper[];
  featureFeedbacks: FeatureFeedback[];
  alternatingPosts: Post[][];
  isCurrentlySearching: boolean;
  isGettingPost: boolean;
  isGettingMoreResults: boolean;
  isGettingCombinedPosts: boolean;
  isGettingFeatureFeedbacks: boolean;
  previousSearch: PostSearchFilter;
  totalCount: number;
}

@State<NewsfeedStateModel>({
  name: 'newsfeed',
  defaults: {
    posts: [],
    combinedPosts: [],
    featureFeedbacks: [],
    alternatingPosts: [],
    isGettingCombinedPosts: false,
    isCurrentlySearching: false,
    isGettingPost: false,
    isGettingMoreResults: false,
    isGettingFeatureFeedbacks: false,
    previousSearch: null,
    totalCount: 0,
  },
})
@Injectable()
export class NewsfeedState {
  private newsfeedService = inject(NewsfeedService);
  private store = inject(Store);
  private notificationService = inject(NotificationService);

  @Action(NewsfeedGetPostAction)
  public newsfeedGetPost(
    { getState, patchState, dispatch }: StateContext<NewsfeedStateModel>,
    action: NewsfeedGetPostAction
  ) {
    const state = getState();
    const postInState = state.posts.find(p => p.slug?.current === action.slug);
    if (postInState) {
      return postInState;
    }

    patchState({ isGettingPost: true });

    return this.newsfeedService.getPost(action.slug).pipe(
      tap(response => dispatch(new NewsfeedGetPostSuccessAction(response))),
      catchError(() => dispatch(new NewsfeedGetPostErrorAction(Messages.GetPostFailed)))
    );
  }

  @Action(NewsfeedGetPostSuccessAction)
  newsfeedGetPostSuccess({ getState, patchState }: StateContext<NewsfeedStateModel>, action: NewsfeedGetPostSuccessAction) {
    const state = getState();

    patchState({
      isGettingPost: false,
      posts: [...state.posts, addRefToPost(action.response)],
      alternatingPosts: alternateDistributionOfPosts([...state.posts, addRefToPost(action.response)]),
    });
  }

  @Action(NewsfeedGetPostErrorAction)
  newsfeedGetPostError({ patchState }: StateContext<NewsfeedStateModel>) {
    patchState({ isGettingPost: false });
  }

  @Action(PostSearchAction)
  postSearch({ dispatch, patchState }: StateContext<NewsfeedStateModel>, action: PostSearchAction) {
    patchState({ isCurrentlySearching: true });

    return this.newsfeedService.postSearch(action.searchRequest).pipe(
      tap(response => dispatch(new PostSearchSuccessAction(response, action.searchRequest))),
      catchError(() => dispatch(new PostSearchFailedAction(Messages.GetPostSearchFailed)))
    );
  }

  @Action(PostSearchSuccessAction)
  postSearchSuccess({ patchState }: StateContext<NewsfeedStateModel>, action: PostSearchSuccessAction) {
    patchState({
      posts: action.searchResponse.data.map(addRefToPost),
      alternatingPosts: alternateDistributionOfPosts(action.searchResponse.data.map(addRefToPost)),
      isCurrentlySearching: false,
      previousSearch: action.searchRequest,
      totalCount: action.searchResponse.totalRows,
    });
  }

  @Action(PostSearchFailedAction)
  postSearchFailed({ patchState }: StateContext<NewsfeedStateModel>, { error }: PostSearchFailedAction) {
    this.notificationService.error(error);
    patchState({ isCurrentlySearching: false });
  }

  @Action(AppendNewPostsAction)
  appendNewPosts({ patchState, getState, dispatch }: StateContext<NewsfeedStateModel>) {
    const state: NewsfeedStateModel = getState();
    const previousSearch = state.previousSearch;

    const newSearch: PostSearchFilter = {
      ...previousSearch,
      currentPage: previousSearch.currentPage ? previousSearch.currentPage + 1 : 1,
    };

    patchState({ isGettingMoreResults: true });

    return this.newsfeedService.postSearch(newSearch).pipe(
      tap(response => dispatch(new AppendNewPostsSuccessAction(response, newSearch))),
      catchError(() => dispatch(new AppendNewPostsFailedAction()))
    );
  }

  @Action(AppendNewPostsSuccessAction)
  appendNewPostsSuccess({ patchState, getState }: StateContext<NewsfeedStateModel>, action: AppendNewPostsSuccessAction) {
    patchState({
      posts: getState().posts.concat(action.searchResponse.data.map(addRefToPost)),
      alternatingPosts: alternateDistributionOfPosts(getState().posts.concat(action.searchResponse.data.map(addRefToPost))),
      previousSearch: action.searchRequest,
      isGettingMoreResults: false,
      totalCount: action.searchResponse.totalRows,
    });
  }

  @Action(AppendNewPostsFailedAction)
  appendNewPostsFailed({ patchState }: StateContext<NewsfeedStateModel>) {
    patchState({ isGettingMoreResults: false });
  }

  @Action(GetCombinedPostsAction)
  getCombinedPosts({ dispatch, patchState }: StateContext<NewsfeedStateModel>) {
    patchState({ isGettingCombinedPosts: true });
    return this.newsfeedService.getCombinedPosts().pipe(
      tap(response => dispatch(new GetCombinedPostsSuccessAction(response))),
      catchError(() => dispatch(new GetCombinedPostsFailedAction()))
    );
  }

  @Action(GetCombinedPostsSuccessAction)
  getCombinedPostsSuccess(
    { patchState }: StateContext<NewsfeedStateModel>,
    { combinedPosts }: GetCombinedPostsSuccessAction
  ) {
    patchState({
      combinedPosts,
      isGettingCombinedPosts: false,
    });
  }

  @Action(GetCombinedPostsFailedAction)
  getCombinedPostsFailed({ patchState }: StateContext<NewsfeedStateModel>) {
    patchState({ isGettingCombinedPosts: false });
  }

  @Action(GetFeatureFeedbacksAction)
  getFeatureFeedbacks({ dispatch, patchState }: StateContext<NewsfeedStateModel>) {
    patchState({ isGettingFeatureFeedbacks: true });
    return this.newsfeedService.getFeatureFeedbacks().pipe(
      tap(response => dispatch(new GetFeatureFeedbacksSuccessAction(response))),
      catchError(() => dispatch(new GetCombinedPostsFailedAction()))
    );
  }

  @Action(GetFeatureFeedbacksSuccessAction)
  getFeatureFeedbacksSuccess(
    { patchState }: StateContext<NewsfeedStateModel>,
    { featureFeedbacks }: GetFeatureFeedbacksSuccessAction
  ) {
    patchState({
      featureFeedbacks,
      isGettingFeatureFeedbacks: false,
    });
  }

  @Action(GetFeatureFeedbacksSuccessAction)
  getFeatureFeedbacksFailed({ patchState }: StateContext<NewsfeedStateModel>) {
    patchState({ isGettingFeatureFeedbacks: false });
  }
}
